import React, { FC, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import { InfoCard, IconTypes } from './InfoCard/InfoCard';
import { useMessageBarStyle } from './MessageBar.style';

import { getErrorMessage } from '@/shared/utils/valiation';
import { BorrowModalData } from '@/shared/validations/borrowModal.schema';
import { useGetRiskLevel } from '@/shared/hooks/useGetRiskLevel';
import { Agreement } from '@/shared/api/borrow';

type MessageBarProps = {
    health: string | null;
    agreement: Agreement;
};

const MessageBar: FC<MessageBarProps> = ({ health, agreement }) => {
    const classes = useMessageBarStyle();

    const { formState } = useFormContext<BorrowModalData>();

    const { errors } = formState;

    const { loading, isHigh, isWarning, isLow } = useGetRiskLevel({
        health,
        marginCallThreshold: agreement.marginCallThreshold,
        liquidationThreshold: agreement.liquidationThreshold,
    });

    const getInfoCardType = useCallback(() => {
        if (errors && Object.keys(errors).length) {
            return IconTypes.ALERT;
        }

        if (isLow) return IconTypes.SUCCESS;
        if (isWarning) return IconTypes.ALERT;
        if (isHigh) return IconTypes.ERROR;
        return IconTypes.INFO;
    }, [errors, isHigh, isWarning, isLow]);

    const getInfoCardText = useCallback(() => {
        if (errors && Object.keys(errors).length) {
            return getErrorMessage(errors);
        }

        if (loading) {
            return 'Add enough collateral to keep your Risk Factor in the Safe zone.';
        }

        if (isLow) {
            return 'Assets are secure, continue monitoring to maintain security.';
        }

        if (isWarning) {
            return 'Your health factor is approaching a risky level. Consider adjusting your collateral to avoid liquidation.';
        }

        if (isHigh) {
            return 'Warning! Your health factor is dangerously low. Immediate action is needed to prevent liquidation.';
        }

        return 'Add enough collateral to keep your Risk Factor in the Safe zone.';
    }, [loading, isLow, isHigh, isWarning, errors]);

    return (
        <InfoCard
            type={getInfoCardType()}
            text={getInfoCardText()}
            classes={{ root: classes.infoCard }}
        />
    );
};

export default MessageBar;
